import { Box, Button, Flex, Input, VStack } from "@chakra-ui/react";
import { flexRender, Table } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useCallback, useState } from "react";

export type Cell = {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any;
};

type ClientSideTableProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>;
  w?: string[];
  flexGrow?: number[];
  toolboxButtons?: boolean;
  actionsComponent?: () => React.ReactNode;
  noDataComponent?: () => React.ReactNode;
  handleCellClick?: (row: Cell) => void;
  noSearch?: boolean;
  noPagination?: boolean;
};

function ClientSideTable({
  table,
  w,
  flexGrow,
  toolboxButtons,
  actionsComponent,
  noDataComponent,
  handleCellClick,
  noSearch = false,
  noPagination = false,
}: ClientSideTableProps) {
  const [globalFilter, setGlobalFilter] = useState("");

  const handlSetGlobalFilter = useCallback(
    (filter: string) => {
      setGlobalFilter(filter);
      table.setGlobalFilter(filter);
    },
    [table]
  );

  return (
    <>
      {/* Toolbar */}
      <Flex my="1rem" justifyContent="space-between">
        <Flex>
          {!noSearch && (
            <Input
              value={globalFilter ?? ""}
              onChange={(evt) => handlSetGlobalFilter(String(evt.target.value))}
              placeholder="Rechercher"
              w="20rem"
            />
          )}
        </Flex>
        {toolboxButtons !== false && (
          <>{actionsComponent && actionsComponent()}</>
        )}
      </Flex>

      <VStack
        alignItems={"start"}
        bg="zinc.50"
        rounded="xl"
        px="4px"
        w="100%"
      >
        <Flex w="100%" lineHeight={"44px"} px="1rem" py="4px">
          {table.getHeaderGroups().map((headerGroup) => {
            return headerGroup.headers.map((header, index) => {
              return (
                <Box
                  w={w ? w[index] : "auto"}
                  flexGrow={flexGrow ? flexGrow[index] : "auto"}
                  key={header.id}
                >
                  <Flex
                    w={"100%"}
                    // key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    justifyContent="space-between"
                    pr="10px"
                    color="gray.500"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: <ChevronUpIcon h="44px"></ChevronUpIcon>,
                      desc: <ChevronDownIcon h="44px"></ChevronDownIcon>,
                    }[header.column.getIsSorted() as string] ?? null}
                  </Flex>
                </Box>
              );
            });
          })}
        </Flex>

        <Flex w="100%" direction={"column"} gap="2px" mb="4px">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <Flex
                  key={row.id}
                  lineHeight={"44px"}
                  alignItems={"center"}
                  bg="white"
                  px="1rem"
                  _first={{ roundedTop: "xl" }}
                  _last={{ roundedBottom: "xl" }}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <Box
                        w={w ? w[index] : "auto"}
                        flexGrow={flexGrow ? flexGrow[index] : "auto"}
                        key={cell.id}
                        onClick={() => {
                          handleCellClick &&
                            handleCellClick({ id: cell.id, row: row.original });
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Box>
                    );
                  })}
                </Flex>
              );
            })
          ) : noDataComponent ? (
            noDataComponent()
          ) : (
            <Flex w="100%" justifyContent="center">
              <Box
                w="100%"
                rounded="xl"
                bg="white"
                textAlign="center"
                h="44px"
                lineHeight="44px"
                color="gray.500"
              >
                Pas de données pour le moment
              </Box>
            </Flex>
          )}
        </Flex>
      </VStack>

      {!noPagination && (
        <Flex w="100%" justifyContent="center" mt="1rem">
          <Box>
            <Button
              className="border rounded p-1"
              onClick={() => table.firstPage()}
              isDisabled={!table.getCanPreviousPage()}
              variant="outline"
              mr="4px"
            >
              {"<<"}
            </Button>
            <Button
              className="border rounded p-1"
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              variant="outline"
            >
              {"<"}
            </Button>
            <Box display="inline" mx="10px">
              Page&nbsp;
              {table.getState().pagination.pageIndex + 1}&nbsp;/&nbsp;
              {table.getPageCount().toLocaleString()}
            </Box>
            <Button
              className="border rounded p-1"
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              variant="outline"
              mr="4px"
            >
              {">"}
            </Button>
            <Button
              className="border rounded p-1"
              onClick={() => table.lastPage()}
              isDisabled={!table.getCanNextPage()}
              variant="outline"
            >
              {">>"}
            </Button>
          </Box>
        </Flex>
      )}
    </>
  );
}
export default ClientSideTable;
